import { FileText, Layers } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { KeyValue } from "../atoms/KeyValue";
import { Link } from "../atoms/Link";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import {
	ReportFilterPanel,
	type ReportFilterPanelProps,
} from "../organisms/ReportFilterPanel";

export interface ReportItem {
	id: string;
	kind: "event" | "loan" | "batch";
	target: string;
	generatedAt: string;
	downloadUrl: string;
}

export interface ReportsListPageProps {
	reports: ReportItem[];
	onNavigateEvents: () => void;
	onNavigateLoans: () => void;
	/** @deprecated Batches are demoted to an advanced view; no longer surfaced here. */
	onNavigateBatches?: () => void;
	filterPanel?: ReportFilterPanelProps;
	className?: string;
}

const KIND_LABELS: Record<ReportItem["kind"], string> = {
	event: "Event",
	loan: "Loan",
	batch: "Batch",
};

function ReportsListPage({
	reports,
	onNavigateEvents,
	onNavigateLoans,
	filterPanel,
	className,
}: ReportsListPageProps): ReactNode {
	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<div className="flex flex-col gap-1">
				<Heading size="xl">Reports & certificates</Heading>
				<Text size="sm" textColor="mutedForeground">
					A certificate is a shareable PDF that proves a loan or event is
					authentic and recorded on an independent public ledger. Certificates
					download to your browser; nothing is stored on the server.
				</Text>
			</div>

			{filterPanel && <ReportFilterPanel {...filterPanel} />}

			<div className="flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-5">
				<Text size="sm" weight="medium" as="span">
					Already viewing a loan or event?
				</Text>
				<Text size="sm" textColor="mutedForeground">
					You can download a certificate directly from any loan or event page —
					no IDs to copy.
				</Text>
				<div className="flex flex-wrap gap-2">
					<Button variant="outline" size="sm" onClick={onNavigateLoans}>
						<Layers className="mr-1 h-4 w-4" aria-hidden="true" />
						Browse loans
					</Button>
					<Button variant="outline" size="sm" onClick={onNavigateEvents}>
						<FileText className="mr-1 h-4 w-4" aria-hidden="true" />
						Browse events
					</Button>
				</div>
			</div>

			{reports.length > 0 && (
				<ul className="flex flex-col gap-3">
					{reports.map((report) => (
						<li
							key={report.id}
							className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
						>
							<div className="flex flex-col gap-2">
								<div className="flex items-center gap-2">
									<Badge variant="secondary">{KIND_LABELS[report.kind]}</Badge>
									<Text size="sm" weight="medium" as="span">
										{report.target}
									</Text>
								</div>
								<KeyValue
									label="Generated"
									value={report.generatedAt}
									direction="horizontal"
								/>
							</div>
							<Button variant="outline" size="sm" asChild={true}>
								<Link href={report.downloadUrl} external={true}>
									Download
								</Link>
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
ReportsListPage.displayName = "ReportsListPage";

export default ReportsListPage;
export { ReportsListPage };
